import parsePhoneNumberFromString, { AsYouType } from 'libphonenumber-js';

export type PhoneNumberType = 'phone' | 'areaCode';

/**
 * Strips the formatting from a phone number string.
 *
 * @param {string} value - The phone number string to strip formatting from (e.g. "(123) 456 - 7890").
 * @returns {string} - The phone number string without formatting (e.g. "1234567890").
 */
export function stripPhoneNumberFormat(value: string): string {
  const phoneNumberSearchAsYouTypeObj = new AsYouType();
  phoneNumberSearchAsYouTypeObj.input(value);
  return phoneNumberSearchAsYouTypeObj.getChars();
}

/**
 * Formats a phone number or area code.
 *
 * @param {string} input - The phone number or area code to format.
 * @returns {string} - The formatted phone number or area code.
 */
export function formatNumberOrAreaCode(input: string): string {
  const phoneNumber = parsePhoneNumberFromString(input, 'US');

  if (phoneNumber?.isValid()) {
    return phoneNumber.formatInternational();
  }

  // If parsing fails, assume it's an area code
  return `+1 (${input}) *`;
}

/**
 * Checks if the input is a valid area code.
 *
 * @param {string} input - The area code to validate.
 * @returns {boolean} - True if the input is a valid area code, false otherwise.
 */
export function isValidAreaCode(input: string): boolean {
  const parsedAreaCode = parsePhoneNumberFromString(input, 'US');
  return parsedAreaCode?.nationalNumber.length === 3 || false;
}

/**
 * Checks if the input is a valid phone number.
 *
 * @param {string} input - The phone number to validate.
 * @returns {boolean} - True if the input is a valid phone number, false otherwise.
 */
export function isValidPhoneNumber(input: string): boolean {
  const phoneNumber = parsePhoneNumberFromString(input, 'US');

  return phoneNumber?.isValid() || false;
}

/**
 * Parses the input to determine if it is a phone number or an area code.
 *
 * @param {string} input - The input to parse.
 * @returns {{ type: PhoneNumberType, number: string }} - An object containing the type ('phone' or 'areaCode') and the parsed number.
 */
export function parseAreaCodeOrPhone(input: string): {
  type: PhoneNumberType;
  number: string;
} {
  const phoneNumber = parsePhoneNumberFromString(input, 'US');

  if (phoneNumber?.isValid()) {
    return { type: 'phone', number: phoneNumber.number };
  } else {
    const areaCode = input.replace(/(^\+1\s*)|(\s)|(\D)/g, '');
    return { type: 'areaCode', number: areaCode };
  }
}
